import React, { useState, useRef, useCallback } from "react";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { DropDownButton } from "@progress/kendo-react-buttons";
import { caretAltDownIcon } from "@progress/kendo-svg-icons";
import { Calendar } from "@progress/kendo-react-dateinputs";
import { ComboBoxLabel } from "components/Dashboard";
import styles from "../../components/Dashboard/index.module.scss";

export const DateRange = ({
  fromDate,
  toDate,
  onDateChange,
  label = "Date Range",
  dropdownItems = [
    "Today",
    "This Week",
    "Past Week",
    "This Month",
    "Quarter",
    "Year",
  ],
  className = "",
  icon = false,
  yearStartDate = null,
  style = {},
  fillWidth = false,
}) => {
  const { height: inputRowHeight, ...rootStyle } = style;
  const pickerWidth = fillWidth ? "100%" : "122px";
  const [showCalender1, setShowCalender1] = useState(false);
  const [showCalender2, setShowCalender2] = useState(false);

  const blurTimeoutRef = useRef(null);

  const CustomCalendar = (props) => {
    return <Calendar {...props} />;
  };

  const onFocus = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
  }, []);

  const onBlurTimeout = useCallback(() => {
    setShowCalender1(false);
    setShowCalender2(false);
  }, []);

  const onBlur = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(onBlurTimeout, 200);
  }, [onBlurTimeout]);

  const onClickFromDate = useCallback((e) => {
    setShowCalender2(false);
    setShowCalender1((prevShow) => !prevShow);
  }, []);

  const onClickToDate = useCallback((e) => {
    setShowCalender1(false);
    setShowCalender2((prevShow) => !prevShow);
  }, []);

  const handleCalendarClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const onChangeDuration = (e) => {
    let newFromDate, newToDate;
    const today = new Date();

    if (e.item === "Today") {
      newFromDate = newToDate = today;
    } else if (e.item === "This Week") {
      newFromDate = today;
      newToDate = new Date(today);
      newToDate.setDate(today.getDate() + 6);
    } else if (e.item === "Past Week") {
      const dayOfWeek = today.getDay();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(
        today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1),
      );
      const startOfPastWeek = new Date(startOfCurrentWeek);
      startOfPastWeek.setDate(startOfCurrentWeek.getDate() - 7);
      const endOfPastWeek = new Date(startOfPastWeek);
      endOfPastWeek.setDate(startOfPastWeek.getDate() + 6);
      newFromDate = startOfPastWeek;
      newToDate = endOfPastWeek;
    } else if (e.item === "This Month") {
      newFromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      newToDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (e.item === "Quarter") {
      if (yearStartDate) {
        const result = getFiscalQuarter(today, yearStartDate);
        newFromDate = result.fromDate;
        newToDate = result.toDate;
      } else {
        const currentMonth = today.getMonth();
        const startMonth = currentMonth - (currentMonth % 3);
        newFromDate = new Date(today.getFullYear(), startMonth, 1);
        newToDate = new Date(today.getFullYear(), startMonth + 3, 0);
      }
    } else if (e.item === "Year") {
      newFromDate = new Date(today.getFullYear(), 0, 1);
      newToDate = new Date(today.getFullYear(), 11, 31);
    }

    onDateChange(newFromDate, newToDate, e.item);
  };

  function getFiscalQuarter(date, fiscalYearStartDate) {
    const fiscalStart = new Date(fiscalYearStartDate);
    const fiscalStartMonth = fiscalStart.getMonth();
    const fiscalStartDay = fiscalStart.getDate();

    // Create fiscal year start for the current period
    let fiscalYearStart;
    if (date.getMonth() >= fiscalStartMonth) {
      fiscalYearStart = new Date(
        date.getFullYear(),
        fiscalStartMonth,
        fiscalStartDay,
      );
    } else {
      // We're before fiscal year start, so we're in previous fiscal year
      fiscalYearStart = new Date(
        date.getFullYear() - 1,
        fiscalStartMonth,
        fiscalStartDay,
      );
    }

    // Calculate months from fiscal year start
    let monthsDiff =
      (date.getFullYear() - fiscalYearStart.getFullYear()) * 12 +
      (date.getMonth() - fiscalYearStart.getMonth());

    // Determine quarter (0-3)
    const quarter = Math.floor(monthsDiff / 3);

    // Calculate quarter boundaries
    const quarterStartMonth = fiscalYearStart.getMonth() + quarter * 3;
    const quarterStartYear =
      fiscalYearStart.getFullYear() + Math.floor(quarterStartMonth / 12);
    const adjustedStartMonth = quarterStartMonth % 12;

    const fromDate = new Date(quarterStartYear, adjustedStartMonth, 1);
    const toDate = new Date(quarterStartYear, adjustedStartMonth + 3, 0);

    return { fromDate, toDate, quarter: quarter + 1 };
  }

  const handleFromDateChange = (e) => {
    const newFromDate = new Date(e.target.value);
    setShowCalender1(false);
    onDateChange(newFromDate, toDate);
  };

  const handleToDateChange = (e) => {
    const newToDate = new Date(e.target.value);
    setShowCalender2(false);
    onDateChange(fromDate, newToDate);
  };

  return (
    <div
      className={`d-flex flex-column ${className}`}
      style={
        fillWidth ? { width: "100%", minWidth: 0, ...rootStyle } : { ...rootStyle }
      }>
      <ComboBoxLabel name={label} icon={icon} />

      <div
        className={fillWidth ? "d-flex w-100" : "d-flex"}
        style={{ minWidth: 0, height: inputRowHeight }}>
        <div
          className={fillWidth ? "flex-grow-1" : ""}
          style={fillWidth ? { minWidth: 0 } : undefined}
          onClick={onClickFromDate}>
          <DatePicker
            onFocus={onFocus}
            onBlur={onBlur}
            calendar={(props) => (
              <div onClick={handleCalendarClick}>
                <CustomCalendar {...props} />
              </div>
            )}
            show={showCalender1}
            className={styles["date-range-picker"]}
            width={pickerWidth}
            value={fromDate}
            onChange={handleFromDateChange}
            toggleButton={() => {
              <></>;
            }}
          />
        </div>

        <div
          className={fillWidth ? "flex-grow-1" : ""}
          style={fillWidth ? { minWidth: 0 } : undefined}
          onClick={onClickToDate}>
          <DatePicker
            onFocus={onFocus}
            onBlur={onBlur}
            calendar={(props) => (
              <div onClick={handleCalendarClick}>
                <CustomCalendar {...props} />
              </div>
            )}
            show={showCalender2}
            className={styles["date-range-picker-right"]}
            width={pickerWidth}
            value={toDate}
            onChange={handleToDateChange}
            toggleButton={() => {
              <></>;
            }}
          />
        </div>
        <DropDownButton
          items={dropdownItems}
          onItemClick={onChangeDuration}
          svgIcon={caretAltDownIcon}
          style={{
            borderRadius: "0px 5px 5px 0px",
            padding: "4px",
            boxShadow: "none",
            height: "auto",
          }}
        />
      </div>
    </div>
  );
};
